import { useCallback, useRef } from 'react';

import get from 'lodash/get';

import { useScreenModalV2 } from '@gogame/screens';

import {
  DIMENSION_AD_SET,
  DIMENSION_PARTNER_AD,
  DIMENSION_PARTNER_CAMPAIGN,
  DIMENSION_PLACEMENT,
  DIMENSION_PUBLISHER,
  DIMENSION_ROOT_AD_SET,
  DIMENSION_ROOT_PARTNER_CAMPAIGN,
  DIMENSION_UNIQUE_PLACEMENT,
  EDIT_BID_AMOUNT,
  EDIT_DAILY_BUDGET,
  EDIT_LIFETIME_BUDGET,
  EDIT_STATUS,
  METRIC_AD_SET_BIDING_STRATEGY,
  METRIC_AD_SET_BID_AMOUNT,
  METRIC_AD_SET_MIN_ROAS_BID,
  PLACEMENT_BID_AMOUNT,
  metricActionsMap,
} from 'scenes/Trackers/Tracker/components/TrackerDataTable/components/EditRow';
import DynamicDataForms, {
  BIDING_STRATEGY,
  BID_AMOUNT,
  DAILY_BUDGET,
  LIFETIME_BUDGET,
  MIN_ROAS_BID,
} from 'scenes/Trackers/Tracker/components/TrackerDataTable/components/EditRow/DynamicDataForms';
import {
  mapDynamicDataToAdSet,
  mapDynamicDataToPartnerAd,
  mapDynamicDataToPartnerCampaign,
  mapDynamicDataToPlacement,
} from 'scenes/Trackers/Tracker/components/TrackerDataTable/components/EditRow/helpers';

import ChangeStatusDialog from '../components/ConfirmChangeStatusDialog';

const normalizeAmount = (input) => input.value || input.compareValue;

export const useEditRow = ({
  onChangeRows,
  dimensionName,
  dimension,
  updateDynamicData,
}) => {
  const dimensionRef = useRef({});
  const showChangeStatusModal = useScreenModalV2(ChangeStatusDialog);
  const showLifeTimeBudget = useScreenModalV2(DynamicDataForms.LifetimeBudget);
  const showDailyBudget = useScreenModalV2(DynamicDataForms.DailyBudget);
  const showBidAmount = useScreenModalV2(DynamicDataForms.BidAmount);

  dimensionRef.current = { id: dimension, name: dimensionName };

  const changeDynamicData = useCallback(
    async (data) => {
      const { id } = dimensionRef.current;
      try {
        const dynamicData = await updateDynamicData({
          dimension: id,
          data,
        });

        switch (id) {
          case DIMENSION_PUBLISHER:
          case DIMENSION_PARTNER_AD:
            return dynamicData.map(mapDynamicDataToPartnerAd);
          case DIMENSION_PARTNER_CAMPAIGN:
          case DIMENSION_ROOT_PARTNER_CAMPAIGN:
            return dynamicData.map(mapDynamicDataToPartnerCampaign);
          case DIMENSION_UNIQUE_PLACEMENT:
          case DIMENSION_PLACEMENT:
            return dynamicData.map(mapDynamicDataToPlacement);
          case DIMENSION_ROOT_AD_SET:
          case DIMENSION_AD_SET:
            return dynamicData.map(mapDynamicDataToAdSet);
          default:
            throw new Error(`unknown dimension id: ${id}`);
        }
      } catch (e) {
        throw e;
      }
    },
    [updateDynamicData]
  );

  const editCell = useCallback(
    ({ col, value, entities, onClose }) => {
      const dimensionName = dimensionRef.current.name;
      switch (metricActionsMap[col]) {
        case EDIT_LIFETIME_BUDGET:
          return showLifeTimeBudget({
            onClose,
            dimensionName,
            entities,
            mapEntityToValues: ({ [col]: budget, creative }) => {
              const { adCampaignId, apiId, setApiId, placementName } = creative;
              return {
                [LIFETIME_BUDGET]: normalizeAmount(budget),
                adCampaignId,
                apiId,
                setApiId,
                placementName,
              };
            },
          });
        case EDIT_DAILY_BUDGET:
          return showDailyBudget({
            onClose,
            dimensionName,
            entities,
            mapEntityToValues: ({ [col]: budget, creative }) => {
              const { adCampaignId, apiId, setApiId, placementName } = creative;
              return {
                [DAILY_BUDGET]: normalizeAmount(budget),
                adCampaignId,
                apiId,
                setApiId,
                placementName,
              };
            },
          });
        case EDIT_BID_AMOUNT: {
          const withBidingStrategy =
            col === METRIC_AD_SET_BID_AMOUNT && entities.length === 1;
          const withMinRoasBid = col === METRIC_AD_SET_BID_AMOUNT;

          return showBidAmount({
            onClose,
            dimensionName,
            entities,
            withBidingStrategy,
            withMinRoasBid,
            updateWarning:
              entities.length > 1 &&
              [METRIC_AD_SET_BID_AMOUNT, PLACEMENT_BID_AMOUNT].includes(col)
                ? 'Warning: only for custom bids. Ads that use auto bidding, will not be affected'
                : '',
            mapEntityToValues: ({
              [col]: amount,
              [METRIC_AD_SET_BIDING_STRATEGY]: bidingStrategy,
              [METRIC_AD_SET_MIN_ROAS_BID]: minRoasBid,
              creative,
            }) => {
              const { adCampaignId, apiId, setApiId, placementName } = creative;

              const v = {
                [BID_AMOUNT]: normalizeAmount(amount),
                adCampaignId,
                apiId,
                setApiId,
                placementName,
              };

              if (withBidingStrategy) {
                v[BIDING_STRATEGY] = get(
                  bidingStrategy,
                  'value',
                  bidingStrategy
                );
              }

              if (withMinRoasBid) {
                v[MIN_ROAS_BID] = get(minRoasBid, 'value', minRoasBid);
              }

              return v;
            },
          });
        }

        case EDIT_STATUS: {
          return showChangeStatusModal({
            onClose,
            dimensionName,
            status: value,
          }).then(() => {
            return entities.map(({ creative }) => ({
              ...creative,
              status: value,
            }));
          });
        }

        default:
          new Error(`unknown metric - ${col}`);
      }
    },
    [dimensionName]
  );

  const handleEditCell = useCallback(
    async ({ col, value, rowData, onStop }) => {
      try {
        const values = await editCell({
          col,
          entities: [rowData],
          value,
          onClose: onStop,
        });
        const [changedData] = await changeDynamicData(values);
        onChangeRows([{ ...rowData, ...changedData }]);
        onStop();
      } catch (e) {
        /* eslint-disable-next-line */
        console.error(e);
        onStop();
      }
    },
    [onChangeRows, editCell]
  );

  const handleEditBulkCells = useCallback(
    async ({ col, value, rows }, processAsync) => {
      try {
        const values = await editCell({
          col,
          entities: rows,
          value,
        });

        const changedRows = await processAsync(changeDynamicData(values));
        onChangeRows(rows.map((row, i) => ({ ...row, ...changedRows[i] })));
      } catch (e) {
        /* eslint-disable-next-line */
        console.error(e);
      }
    },
    [onChangeRows, editCell]
  );

  return {
    onEditCell: handleEditCell,
    onEditBulkCells: handleEditBulkCells,
  };
};
